import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import type { Analyse } from "../types/analyse";
import StandardPDFLayout from "./StandardPDFLayout";

interface AnalysesPDFProps {
  analyses: Analyse[];
  patientInfo: {
    nom: string;
    prenom: string;
    dateNaissance: string;
    adresse?: string;
  };
  doctorInfo: {
    firstName: string;
    lastName: string;
    title: string;
    specialty?: string; // ✅ Ajouter la spécialité du médecin
  };
  consultationDate: string;
  analysisNumber?: string;
  instructions?: string; // ✅ Ajout des instructions
}

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 9,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  container: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  headerText: {
    textAlign: "right",
    flex: 1,
    paddingLeft: 10,
  },
  patientSection: {
    backgroundColor: "#fafbff",
    padding: 5,
    marginBottom: 6,
    borderRadius: 3,
    borderLeftWidth: 2,
    borderLeftColor: "#113285",
  },
  patientTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  patientGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  patientItem: {
    width: "48%",
    marginBottom: 1,
  },
  patientItemFull: {
    width: "100%",
    marginBottom: 1,
  },
  patientLabel: {
    fontSize: 6,
    color: "#6b7280",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },
  patientValue: {
    fontSize: 8,
    color: "#111827",
    fontWeight: "normal",
    marginTop: 0.5,
  },
  analysesSection: {
    marginBottom: 8,
  },
  analysesTitle: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 5,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingBottom: 2,
  },
  analysesList: {
    paddingLeft: 0,
  },
  analyseItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 2,
    paddingVertical: 1,
  },
  analyseNumber: {
    width: 15,
    fontSize: 7,
    fontWeight: "bold",
    color: "#113285",
    textAlign: "right",
    marginRight: 6,
  },
  analyseContent: {
    flex: 1,
    flexDirection: "column",
    flexWrap: "wrap",
  },
  analyseName: {
    fontSize: 8,
    color: "#111827",
    fontWeight: "bold",
    marginBottom: 1,
    lineHeight: 1.2,
  },
  analyseReason: {
    fontSize: 7,
    color: "#6b7280",
    fontStyle: "normal",
    lineHeight: 1.1,
  },
  instructionsSection: {
    marginBottom: 8,
    backgroundColor: "#fef3c7",
    padding: 5,
    borderRadius: 3,
    borderLeftWidth: 2,
    borderLeftColor: "#f59e0b",
  },
  instructionsTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#92400e",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },
  instructionsText: {
    fontSize: 7,
    color: "#451a03",
    lineHeight: 1.2,
  },
  watermark: {
    position: "absolute",
    opacity: 0.04,
    width: 200,
    height: 200,
    left: 60,
    top: 140
  },
});

const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? dateString : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const AnalysesContent: React.FC<AnalysesPDFProps> = ({
  analyses,
  patientInfo,
  doctorInfo,
  consultationDate,
  analysisNumber,
  instructions // ✅ Ajout du paramètre instructions
}) => {
  // Debug: Vérifier les instructions reçues
  const instructionsToUse = instructions || "";
  if (instructions && instructions.trim()) {
    console.log("📄 PDF Instructions reçues:", instructionsToUse);
  }
  console.log("📄 PDF Analyses reçues:", analyses);
  
  // Debug logs pour vérifier les données
  
  
  

  // Fallback if no number provided
  const displayTitle = "DEMANDE D'ANALYSES COMPLÉMENTAIRES";
  const displayNum = analysisNumber || `AN-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000)}`;

  const getPatientAge = (dob: string | undefined | null) => {
    if (!dob) return "Non spécifié";
    
    // Gérer différents formats de date
    let birthDate: Date;
    
    // Format ISO: YYYY-MM-DD
    if (dob.includes('-')) {
      const dateParts = dob.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1;
        const day = parseInt(dateParts[2]);
        birthDate = new Date(year, month, day);
      } else {
        birthDate = new Date(dob);
      }
    } else {
      // Autres formats
      birthDate = new Date(dob);
    }
    
    if (isNaN(birthDate.getTime())) return "Date invalide";
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age + " ans";
  };

  const patientAge = getPatientAge(patientInfo.dateNaissance);

  const currentDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <StandardPDFLayout
      title={displayTitle}
      documentNumber={displayNum}
      documentDate={consultationDate}
    >
      {/* Section patient ultra-compacte */}
      <View style={styles.patientSection}>
        <Text style={styles.patientTitle}>INFORMATIONS PATIENT</Text>
        <View style={styles.patientGrid}>
          <View style={styles.patientItem}>
            <Text style={styles.patientLabel}>NOM COMPLET</Text>
            <Text style={styles.patientValue}>
              {patientInfo.nom} {patientInfo.prenom}
            </Text>
          </View>
          <View style={styles.patientItem}>
            <Text style={styles.patientLabel}>DATE DE NAISSANCE</Text>
            <Text style={styles.patientValue}>
              {formatDate(patientInfo.dateNaissance)} ({patientAge})
            </Text>
          </View>
        </View>
      </View>

      {/* Liste des analyses ultra-compacte */}
      <View style={styles.analysesSection}>
        <Text style={styles.analysesTitle}>ANALYSES DEMANDÉES</Text>
        <View style={styles.analysesList}>
          {analyses.map((analyse, index) => (
            <View key={`${analyse.id || index}`} style={styles.analyseItem}>
              <Text style={styles.analyseNumber}>{index + 1}.</Text>
              <View style={styles.analyseContent}>
                <Text style={styles.analyseName}>
                  {analyse.label || analyse.nom}
                </Text>
                {analyse.reason && (
                  <Text style={styles.analyseReason}>
                    * {analyse.reason}
                  </Text>
                )}
                {analyse.conseils && (
                  <Text style={{ fontSize: 6, color: "#6b7280", fontStyle: "italic", marginTop: 1 }}>
                    Conseils: {analyse.conseils}
                  </Text>
                )}
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Section Instructions - si présentes */}
      {instructionsToUse && (
        <View style={styles.instructionsSection}>
          <Text style={styles.instructionsTitle}>INSTRUCTIONS SPÉCIALES</Text>
          <Text style={styles.instructionsText}>{instructionsToUse}</Text>
        </View>
      )}

      {/* Signature et cachet - même disposition que l'ordonnance */}
      <View style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'flex-end' }}>
        <View style={{ width: '50%', alignItems: 'flex-end' }}>
          {/* Zone de signature et cachet */}
          <View style={{ marginBottom: 25, width: '100%' }}>
            <Text style={{ fontSize: 6, color: "#4a5568", textAlign: 'right', marginBottom: 3 }}>Signature et cachet du médecin</Text>
          </View>

          {/* Informations du médecin */}
          <View style={{ textAlign: 'right', width: '100%' }}>
            <Text style={{ fontSize: 7, fontWeight: "bold", color: "#113285", marginBottom: 1, textAlign: 'right' }}>
              {doctorInfo.title} {doctorInfo.firstName} {doctorInfo.lastName.toUpperCase()}
            </Text>
            <Text style={{ fontSize: 6, color: "#4b5563", marginBottom: 1, textAlign: 'right' }}>
              {doctorInfo.specialty || "Médecin Généraliste"}
            </Text>
            <Text style={{ fontSize: 6, color: "#6b7280", marginTop: 1, textAlign: 'right' }}>Fait le {currentDate}</Text>
          </View>
        </View>
      </View>

      {/* Le pied de page est géré par StandardPDFLayout */}
    </StandardPDFLayout>
  );
};

const AnalysesPDF: React.FC<AnalysesPDFProps> = (props) => (
  <Document>
    <Page size="A5" style={styles.page}>
      <AnalysesContent {...props} />
    </Page>
  </Document>
);

export default AnalysesPDF;